import sortBy from "lodash/sortBy";
import React from "react";

import { useScoped } from "~/tools/impulse";

import type { MonitorCheckStatus, MonitorCheckStatusRelatedFailures } from "../../../../../../common/openapi";
import { ComponentLinkWithType } from "../../../../../../components/react/component-link/ComponentLinkWithType";
import { useExplorationDrawerState } from "../../../../../../components/react/drawer/use-exploration-drawer-state";
import { GenericErrorMessage } from "../../../../../../components/react/error/GenericErrorMessage";
import { LoadingSpinner } from "../../../../../../components/react/loading-spinner/LoadingSpinner";
import { useQueryMonitorCheckStatusRelatedFailures } from "../../../../../../queries/monitors";
import { RelatedHealthViolation } from "../../../../highlights-perspective/monitor-status-drawer/RelatedHealthViolation";

import { useMonitorStatusContext } from "./_monitor-status-context";

export interface MonitorStatusRelatedViolationsProps {
	monitorStatus: MonitorCheckStatus;
}

export const MonitorStatusRelatedViolations: React.FC<MonitorStatusRelatedViolationsProps> = ({ monitorStatus }) => {
	const { timeRange } = useMonitorStatusContext();
	const timeRangeValue = useScoped(timeRange);
	const explorationDrawer = useExplorationDrawerState();
	const {
		data: checkStatuses,
		isLoading,
		error,
	} = useQueryMonitorCheckStatusRelatedFailures(
		{
			identifier: monitorStatus.identifier,
			timestamp: monitorStatus.topologyTime,
		},
		{
			select: React.useCallback(
				({ checkStatuses }: MonitorCheckStatusRelatedFailures) => sortBy(checkStatuses, ["topology.distance", "name"]),
				[],
			),
		},
	);

	if (isLoading) {
		return <LoadingSpinner />;
	}

	if (error || checkStatuses == null) {
		return <GenericErrorMessage error={error} />;
	}

	if (checkStatuses.length === 0) {
		return (
			<div className="px-4 py-6 text-center text-13 text-gray-600 leading-[21px]">
				There are no related health violations on this component or on its dependencies, impacted or related components
			</div>
		);
	}

	return (
		<div className="divide-y divide-solid divide-gray-300" data-testid="monitor-status-related-violations">
			{checkStatuses.map((checkStatus) => (
				<RelatedHealthViolation
					className="border-r-0 border-l-0 py-3 first:pt-0 last:pb-0"
					item={checkStatus}
					key={checkStatus.identifier}
					onMessageClick={() => {
						explorationDrawer.openMonitorStatus({
							identifier: checkStatus.identifier,
							timestamp: monitorStatus.topologyTime,
							timeRange: timeRangeValue,
						});
					}}
					showTriggeredTimestamp={false}
				>
					on{" "}
					<ComponentLinkWithType
						identifier={checkStatus.component.identifier}
						name={checkStatus.component.name}
						timeRange={timeRangeValue}
						timestamp={monitorStatus.topologyTime}
						type={checkStatus.component.type}
					/>
				</RelatedHealthViolation>
			))}
		</div>
	);
};
